<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：管理対象割当マスタ画面
 *
 * 作成者：2021/12/28 ノア）黄
 * 最終修正者：2022/1/27 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span class="tti-viewTitle">{{
              $t('master.managementTargetAllocationMaster.viewTitle', [managementTargetShowName])
            }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-lr-container">
        <!-- 営業所階層一覧 -->
        <div class="tti-tree left">
          <div class="tti-content-title">
            <span>{{ $t('master.managementTargetAllocationMaster.salesOfficeAllocationList') }}</span>
          </div>
          <div class="content">
            <dragableTree :disabled="pageLock" :data="masterTree"></dragableTree>
          </div>
        </div>
        <!-- 管理対象一覧 -->
        <div class="tti-tree right">
          <div class="tti-content-title">
            <span>{{
              $t('master.managementTargetAllocationMaster.managementTargetList', [managementTargetShowName])
            }}</span>
          </div>
          <div class="content">
            <div @click="showTarget = !showTarget">
              <span class="expander">
                <span v-if="showTarget"><tti-svg-icon icon-name="su_021"></tti-svg-icon></span>
                <span v-else><tti-svg-icon icon-name="su_022"></tti-svg-icon></span>
              </span>
              <span>{{ $t('master.managementTargetAllocationMaster.unAllocation') }}</span>
            </div>
            <div class="customTree">
              <dragableTree :disabled="pageLock" v-show="showTarget" :data="unAllocationMasterData"></dragableTree>
              <div class="tree-empty-text" v-if="!unAllocationMasterData || unAllocationMasterData.length === 0">
                {{ $t('messages.common.emptyTableData') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
      </div>
      <div class="tti-bottom-right-area">
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component } from 'vue-property-decorator'
import { cloneDeep, findIndex } from 'lodash'

import MST_BASE from '@/views/master/MST_BASE.vue'
import DragableTree from '@/components/DraggableTree.vue'
import { i18n } from '@/locales'
import { getManagementTargetAllocationMasterData, updateManagementTargetAllocationMasterData } from '@/store/apis'
import {
  TreeData,
  ManagementTargetAllocData,
  ManagementTargetMasterData,
  AllocationOfficeItem,
  MasterUpdateParam,
  ResponseData
} from '@/models'
import { ManagementAllocType } from '@/commons/constants'
import { localService } from '@/commons/services/localService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { AxiosResponse } from 'axios'
import { changeBlobToJson } from '@/store/apis/apiClient'

/**
 * 管理対象割当マスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-mgmt-trgts-alloc',
  components: { DragableTree }
})
export default class MST_MGMT_TRGTS_ALLOC extends MST_BASE {
  /**
   * 画面ID
   */
  pageId = 'MST_MGMT_TRGTS_ALLOC'

  /**
   * 画面名
   */
  pageName = i18n.t('master.managementTargetAllocationMaster.viewTitle').toString()

  /**
   * 管理対象割当リスト
   */
  managementTargetAllocData: ManagementTargetAllocData[] = []

  /**
   * エリアマスタTree
   */
  masterTree: TreeData[] = []

  /**
   * 未割当Tree
   */
  unAllocationMasterData: TreeData[] = []

  showTarget = true

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('MST_MGMT_TRGTS_ALLOC > mounted')
    this.fetchTableData()
    this.checkPageLock()
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<boolean> {
    try {
      const data = (await getManagementTargetAllocationMasterData()) as ManagementTargetAllocData
      this.initAllocData(data)
      this.fetchTableDataAfter()
      return true
    } catch (e) {
      this.$log.debug('MST_MGMT_TRGTS_ALLOC > fetchTableData: e=', e)
      throw e
    }
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {ManagementTargetAllocData} data 検索されたデータ
   * @returns {void}
   */
  initAllocData(data: ManagementTargetAllocData): void {
    if (!data) {
      data = {} as ManagementTargetAllocData
    }
    this.oldData = cloneDeep(data)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.tableData = data
  }

  fetchTableDataAfter(): void {
    this.multipleSelection = []
    this.changeManagementTargetAllocDataToTree()
  }

  /**
   * マスタをTreeDataに変換する
   *
   * @returns {void}
   */
  // eslint-disable-next-line max-lines-per-function
  changeManagementTargetAllocDataToTree(): void {
    this.$log.debug('MST_PREF_ALLOC > changeManagementTargetAllocDataToTree')
    const fillterOfficeData = (this.tableData as unknown as ManagementTargetAllocData).businessOfficeList.map(item => {
      const managementTargetList = item.managementTargetList

      return {
        id: item.businessOfficeId,
        label: `${item.businessOfficeName}(${item.businessOfficeCode})`,
        expand: true,
        disabled: true,
        isLastNode: false,
        parentId: item.parentId,
        type: ManagementAllocType.BUSINESS_OFFICE,
        children:
          managementTargetList?.map(managementTarget => {
            return {
              id: managementTarget.managementTargetId,
              label: `${managementTarget.managementTargetName}(${managementTarget.managementTargetCode})`,
              expand: true,
              disabled: false,
              isLastNode: true,
              src: 'wo_040',
              type: ManagementAllocType.MANAGEMENT_TARGET,
              children: []
            }
          }) || []
      } as unknown as TreeData
    })

    const masterTree: TreeData[] = []
    const map: Record<string, TreeData> = {}

    fillterOfficeData.forEach(item => {
      if (item.id) {
        map[item.id] = item
      }
    })

    fillterOfficeData.forEach(item => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const parent = map[item.parentId]
      if (parent) {
        ;(parent.children || (parent.children = [])).push(item)
      } else {
        if (item.id === localService.userInfo?.businessOfficeId) {
          item.level = '0'
          masterTree.push(item)
        }
      }
    })

    if (!masterTree || masterTree.length === 0) {
      return
    }

    this.masterTree = masterTree as TreeData[]

    this.unAllocationMasterData = (this.tableData as unknown as ManagementTargetAllocData).unAllocationList.map(
      item => {
        return {
          id: item.managementTargetId,
          label: `${item.managementTargetName}(${item.managementTargetCode})`,
          src: 'wo_040',
          disabled: false,
          isLastNode: true,
          type: ManagementAllocType.MANAGEMENT_TARGET,
          children: []
        } as unknown as TreeData
      }
    )
  }

  /**
   * 取消ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCancelButtonClick(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onCancelButtonClick`)

    if (!this.checkDataChanged()) {
      return
    }

    const confirmCallback = () => {
      this.tableData = cloneDeep(this.oldData)
      this.changeManagementTargetAllocDataToTree()
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.cancelConfirm').toString(), confirmCallback)
  }

  /**
   * 変更されましたデータを算出する。
   *
   * @returns {any} 変更されたデータ
   */
  // eslint-disable-next-line
  getChangedData(): any {
    const masterTree = [] as AllocationOfficeItem[]
    this.$log.debug('MST_PREF_ALLOC > getChangedData > this.masterTree=', this.masterTree)
    this.changeTreeToList(masterTree, this.masterTree[0])
    this.$log.debug('MST_PREF_ALLOC > getChangedData > masterTree=', masterTree)

    const updateDataList: { businessOfficeId: string; managementTargetId: string }[] = []

    this.$log.debug('MST_PREF_ALLOC > getChangedData > tableData=', this.tableData)
    ;(this.tableData as unknown as ManagementTargetAllocData).businessOfficeList.forEach(item => {
      const businessOfficeId = item.businessOfficeId
      const oldManagementTargetList = item.managementTargetList
      const newManagementTargetList = masterTree.find(
        master => master.businessOfficeId === businessOfficeId
      )?.managementTargetList

      if (newManagementTargetList) {
        newManagementTargetList.forEach((newManagementTarget: ManagementTargetMasterData) => {
          const index = oldManagementTargetList?.findIndex(
            oldManagementTarget => oldManagementTarget.managementTargetId === newManagementTarget?.managementTargetId
          )
          if (index === -1) {
            updateDataList.push({
              businessOfficeId: businessOfficeId,
              managementTargetId: newManagementTarget?.managementTargetId || ''
            })
          }
        })
      }
    })

    const unAllocationList: string[] = []

    this.unAllocationMasterData.forEach(item => {
      const managementTargetId = item.id
      const index = (this.tableData as unknown as ManagementTargetAllocData).unAllocationList.findIndex(
        unAllocation => unAllocation.managementTargetId === managementTargetId
      )
      if (index === -1) {
        unAllocationList.push(managementTargetId)
      }
    })

    return {
      updateDataList,
      unAllocationList
    }
  }

  changeTreeToList(finallList: AllocationOfficeItem[], target: TreeData): void {
    if (!target) {
      return
    }

    const children = target.children
    const office = {
      businessOfficeId: target.id,
      managementTargetList: [] as ManagementTargetMasterData[]
    }

    children?.forEach(item => {
      if (item.type === ManagementAllocType.MANAGEMENT_TARGET) {
        office.managementTargetList.push({
          managementTargetId: item.id
        } as ManagementTargetMasterData)

        if (findIndex(finallList, { businessOfficeId: office.businessOfficeId }) === -1) {
          finallList.push(office)
        }
      } else {
        this.changeTreeToList(finallList, item)
      }
    })
  }

  /**
   * データが変更されましたかをチェックする。
   *
   * @returns {boolean} 変更されているかどうか
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  checkDataChanged(): boolean {
    const { updateDataList, unAllocationList } = this.getChangedData()

    if ((!updateDataList || updateDataList.length === 0) && (!unAllocationList || unAllocationList.length === 0)) {
      return false
    }

    return true
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onSaveButtonClick(): Promise<void> {
    this.$log.debug(`MST_BASE > ${this.pageId} > onSaveButtonClick`)
    await this.saveTableData()
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {Promise<boolean>} 保存結果
   */
  async saveTableData(): Promise<boolean> {
    try {
      this.$log.debug(`MST_BASE > ${this.pageId} > saveTableData`)

      const { updateDataList, unAllocationList } = this.getChangedData()

      if ((!updateDataList || updateDataList.length === 0) && (!unAllocationList || unAllocationList.length === 0)) {
        msgBoxService.info(i18n.t('messages.masterCommon.dataNotChanged').toString())
        return false
      }

      const params = {
        updateDataList,
        unAllocationList
      } as MasterUpdateParam

      await updateManagementTargetAllocationMasterData(params)
      this.saveTableDataAfter()

      return true
    } catch (e) {
      this.$log.debug(`MST_MGMT_TRGTS_ALLOC > ${this.pageId} > saveTableData: e=`, e)
      const err = e as AxiosResponse<ResponseData>
      changeBlobToJson(err).then(commonResponse => {
        if (commonResponse.common.code === 'E00093') {
          this.fetchTableData()
        }
      })
      throw e
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.customTree {
  height: inherit;

  > ul {
    padding-left: 0;
    margin-top: 0;
    white-space: nowrap;
  }
}

.tti-tree {
  overflow: auto;

  .tti-content-title {
    font-size: $FS02;
  }

  ::v-deep ul {
    padding-left: 24px;
  }

  ::v-deep .content {
    > ul {
      padding-left: 0;
      margin-top: 0;
      white-space: nowrap;
    }

    span {
      display: inline-block;
      padding: 4px 0;
    }
  }
}
</style>
